import type { z } from 'zod'

import type { ApiRequest } from '~/types/ApiRequest'
import { requestAndParse } from '~/services/api/requestAndParse'
import type { ApiRequestMethod } from '~/types/ApiRequestMethod'

/** A fixed URL, or a URL built from a strict-parsed path value. */
type ResourceRoute<PathSchema extends z.ZodObject> =
  | string
  | {
      readonly schema: PathSchema
      readonly value: z.input<PathSchema>
      readonly buildUrl: (path: z.output<PathSchema>) => string
    }

/** An already-parsed request input sent as the body or the query. */
interface ParsedInput {
  readonly channel: 'body' | 'query'
  readonly value: object
}

const requestOptions = (method: ApiRequestMethod, input?: ParsedInput) => {
  if (input === undefined) return { method }
  return input.channel === 'body' ? { body: input.value, method } : { method, query: input.value }
}

/** Resolves the route (strict-parsing any path), fetches (with an already-parsed input when given), strict-parses the success. */
export async function requestGeneratedResource<
  PathSchema extends z.ZodObject,
  SuccessSchema extends z.ZodType
>(
  client: ApiRequest,
  route: ResourceRoute<PathSchema>,
  successSchema: SuccessSchema,
  method: ApiRequestMethod,
  input?: ParsedInput
): Promise<z.output<SuccessSchema>> {
  // `.strict()` widens the static shape; the cast preserves this schema's output
  // type for the URL builder without changing the parsed value.
  const url =
    typeof route === 'string'
      ? route
      : route.buildUrl(route.schema.strict().parse(route.value) as z.output<PathSchema>)
  return requestAndParse(client, successSchema, url, requestOptions(method, input))
}
